import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Heart, CalendarDays, Gift } from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen hero-gradient" data-testid="landing-page">
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-2xl rose-glow flex items-center justify-center shadow-lg shadow-rose-500/30">
              <Sparkles className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <div>
              <span className="font-display text-xl block leading-none">CelebrAI</span>
              <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-charcoal/60 hidden sm:block mt-0.5">Every celebration, perfectly planned.</span>
            </div>
          </div>
          <nav className="flex items-center gap-3">
            <Link to="/login" className="pill-btn pill-btn-secondary text-sm" data-testid="nav-login">Sign in</Link>
            <Link to="/signup" className="pill-btn pill-btn-primary text-sm" data-testid="nav-signup">Get started</Link>
          </nav>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 pt-16 pb-24 grid lg:grid-cols-2 gap-16 items-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <span className="inline-block text-xs uppercase tracking-[0.2em] font-semibold text-terracotta mb-6">Jaipur · Version 1</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-charcoal">
            Never miss the moments <br /><span className="font-serif-italic text-shimmer">that matter</span>.
          </h1>
          <p className="mt-8 text-lg text-charcoal/75 leading-relaxed max-w-lg">
            Your personal AI assistant for celebrating the people you love. Remember birthdays &amp; anniversaries, generate a thoughtful plan, and book flowers, cakes and decor across Jaipur — in one place.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/signup" className="pill-btn pill-btn-primary" data-testid="hero-cta-primary">Begin your first celebration</Link>
            <Link to="/login" className="pill-btn pill-btn-secondary" data-testid="hero-cta-secondary">I already have an account</Link>
          </div>
          <div className="mt-10 flex gap-8 text-sm text-charcoal/60">
            <div><span className="font-display text-2xl text-charcoal block">200+</span>local partners</div>
            <div><span className="font-display text-2xl text-charcoal block">4.9<span className="text-amber-500">★</span></span>concierge rating</div>
            <div><span className="font-display text-2xl text-charcoal block">1 hr</span>average response</div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }} className="relative">
          <div className="absolute -top-6 -left-6 w-full h-full rounded-3xl rose-glow opacity-25" />
          <img
            src="https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1200&q=80"
            alt="Rose petals and candles"
            className="relative rounded-3xl w-full aspect-[4/5] object-cover shadow-2xl"
          />
          <div className="absolute -bottom-6 -right-6 card-surface p-5 max-w-xs">
            <p className="text-xs uppercase tracking-[0.2em] text-terracotta font-semibold mb-2">AI Plan Preview</p>
            <p className="font-display text-lg text-charcoal leading-snug">"A candlelit evening with red roses &amp; her favourite dark truffle cake."</p>
          </div>
        </motion.div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20">
        <h2 className="font-display text-3xl lg:text-4xl mb-12 text-charcoal">A quiet, thoughtful <span className="font-serif-italic text-terracotta">assistant</span>.</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Heart, title: "Remember what matters", body: "Every birthday, anniversary and preference in one calm dashboard.", tint: "cat-anniversary" },
            { icon: Sparkles, title: "AI celebration plans", body: "Personalised gifts, messages and checklists — tuned to their tastes.", tint: "cat-luxury" },
            { icon: Gift, title: "Booked across Jaipur", body: "Flowers, cakes, decor and hampers — arranged by our concierge team.", tint: "cat-cakes" },
          ].map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="card-surface p-8">
              <div className={`w-12 h-12 rounded-2xl ${f.tint} flex items-center justify-center mb-6`}>
                <f.icon className="w-6 h-6" strokeWidth={1.75} />
              </div>
              <h3 className="font-display text-xl mb-2 text-charcoal">{f.title}</h3>
              <p className="text-charcoal/70 leading-relaxed">{f.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <footer className="max-w-7xl mx-auto px-6 py-10 text-sm text-charcoal/60 border-t border-warm mt-8">
        © {new Date().getFullYear()} CelebrAI · Made with intention in Jaipur
      </footer>
    </div>
  );
}
